import { AnimatePresence, motion } from 'framer-motion';
import { Minus } from 'lucide-react';
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/dashboard-icons';
import { StreamingAssistantMessage } from '@/components/chat/shared/streaming-assistant-message';
import { getStreamableMessageContent } from '@/components/chat/shared/streaming-chat-message';
import { TypingIndicator } from '@/components/chat/shared/typing-indicator';
import { useStreamingChatScroll } from '@/components/chat/shared/use-streaming-chat-scroll';
import { useSiteChat } from '@/context/site-chat-context';
import { useTenant } from '@/context/tenant-context';
import { useAgentChatStream } from '@/hooks/use-agent-chat-stream';
import { cn } from '@/lib/tw-merge/cn';

interface AgentAssistantBarProps {
  /** Lowercase agent key used by the chat adapter to route messages. */
  agentKey: string;
  /** Display name shown in the trigger pill and chat header. */
  agentName: string;
  /** Avatar image — prefer the agent's thumbnail or webp portrait. */
  avatar: string;
  /** Optional welcome bubble. Defaults to "Hi, I'm <Name>. How can I help?" */
  welcomeMessage?: string;
  /** Optional suggested-action chips. Pass an empty array to hide. */
  suggestedActions?: string[];
  /** Screen corner where the floating bar should sit. Defaults to bottom right. */
  placement?: 'bottom-left' | 'bottom-right';
}

/**
 * Sticky floating chat bar for an arbitrary agent. Mirrors the Regis bar
 * UI/UX exactly — the only difference is which agent claims the active
 * slot and gets messages routed to it. Render this on per-agent pages
 * and suppress `RegisAssistantBar` for the same route so the two bars
 * don't stack visually.
 */
export default function AgentAssistantBar({
  agentKey,
  agentName,
  avatar,
  welcomeMessage,
  suggestedActions = [],
  placement = 'bottom-right',
}: AgentAssistantBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isBottomLeft = placement === 'bottom-left';

  const { adapter } = useSiteChat();
  const { activeAgent, setActiveAgent } = useTenant();
  const { sendMessage, sendMessageAs, inputValue, setInputValue } = adapter;
  // Read messages + loading scoped to this specific agent — never mirror
  // whatever agent currently holds the active-agent slot. (Without this,
  // navigating from a page where Regis is active to /agents/scyra would
  // show Regis's conversation in Scyra's bar.)
  const { messages: agentMessages, isLoading: isAgentLoading } =
    useAgentChatStream(agentKey);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resolvedWelcome =
    welcomeMessage ?? `Hi, I'm ${agentName}. How can I help today?`;

  // Claim this agent as active ONCE on mount (only if no one else has
  // claimed first). Mirrors the one-shot pattern in RegisAssistantBar
  // so the two bars don't tug-of-war over the active slot.
  const hasClaimedOnMountRef = useRef(false);
  useEffect(() => {
    if (hasClaimedOnMountRef.current) return;
    hasClaimedOnMountRef.current = true;
    if (activeAgent === agentKey) return;
    void setActiveAgent(agentKey).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleMessages = useMemo(
    () => agentMessages.filter(msg => msg.id !== 'welcome-message'),
    [agentMessages]
  );

  useStreamingChatScroll({
    containerRef: messagesContainerRef,
    messages: visibleMessages,
    enabled: isOpen,
    isLoading: isAgentLoading,
  });

  const handleSendMessage = useCallback(
    async (nextMessage: string) => {
      const trimmed = nextMessage.trim();
      if (!trimmed) return;

      // Send explicitly *as* this bar's agent. `sendMessageAs` claims
      // the agent then dispatches with the key baked in, so the message
      // can't land in another agent's queue (which is what happened
      // when this used `sendMessage(trimmed)` after `await
      // setActiveAgent(...)` — sendMessage's closure still held the
      // previous active agent).
      if (sendMessageAs) {
        await sendMessageAs(agentKey, trimmed);
      } else {
        if (activeAgent !== agentKey) {
          try {
            await setActiveAgent(agentKey);
          } catch {
            return;
          }
        }
        sendMessage(trimmed, agentKey);
      }
      setInputValue('');
    },
    [
      activeAgent,
      agentKey,
      setActiveAgent,
      sendMessage,
      sendMessageAs,
      setInputValue,
    ]
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleSendMessage(inputValue);
    },
    [handleSendMessage, inputValue]
  );

  const handleTextareaKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      void handleSendMessage(inputValue);
    },
    [handleSendMessage, inputValue]
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 144)}px`;
  }, [inputValue]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-label={
          isOpen
            ? `Close ${agentName} assistant`
            : `Open ${agentName} assistant`
        }
        className={cn(
          'hover:bg-white/20 fixed bottom-4 z-50 flex h-[50px] w-fit items-center gap-2 rounded-3xl border border-[#00000014] bg-surface-subtle/20 p-2 backdrop-blur-md transition-colors sm:bottom-8 sm:h-[66px] sm:w-fit sm:rounded-3xl sm:p-4',
          isBottomLeft ? 'left-4 sm:left-8' : 'right-4 sm:right-8'
        )}
      >
        <div className="h-8 w-8 overflow-hidden rounded-full bg-[#F0EEDD] p-1">
          <img
            src={avatar}
            alt={`${agentName} avatar`}
            aria-hidden="true"
            width={32}
            height={32}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover sm:h-8 sm:w-8"
          />
        </div>
        <span className="text-title pr-1 text-xs font-medium sm:pr-2 sm:text-base">
          {agentName}
        </span>
        <svg
          className="h-5 w-5 sm:h-[22px] sm:w-[22px]"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.24999 9.625C8.24999 9.44266 8.32242 9.26779 8.45135 9.13886C8.58028 9.00993 8.75515 8.9375 8.93749 8.9375H13.0625C13.2448 8.9375 13.4197 9.00993 13.5486 9.13886C13.6776 9.26779 13.75 9.44266 13.75 9.625C13.75 9.80733 13.6776 9.9822 13.5486 10.1111C13.4197 10.2401 13.2448 10.3125 13.0625 10.3125H8.93749C8.75515 10.3125 8.58028 10.2401 8.45135 10.1111C8.32242 9.9822 8.24999 9.80733 8.24999 9.625ZM8.93749 11.6875C8.75515 11.6875 8.58028 11.7599 8.45135 11.8889C8.32242 12.0178 8.24999 12.1927 8.24999 12.375C8.24999 12.5573 8.32242 12.7322 8.45135 12.8611C8.58028 12.9901 8.75515 13.0625 8.93749 13.0625H11.6875C11.8698 13.0625 12.0447 12.9901 12.1736 12.8611C12.3026 12.7322 12.375 12.5573 12.375 12.375C12.375 12.1927 12.3026 12.0178 12.1736 11.8889C12.0447 11.7599 11.8698 11.6875 11.6875 11.6875H8.93749ZM2.74999 11C2.75034 9.18368 3.35007 7.41825 4.45615 5.97757C5.56224 4.53689 7.11284 3.50149 8.86742 3.03198C10.622 2.56248 12.4825 2.68512 14.1602 3.38088C15.838 4.07664 17.2393 5.30662 18.1467 6.88003C19.0541 8.45344 19.4169 10.2823 19.1788 12.083C18.9407 13.8836 18.1151 15.5553 16.83 16.8389C15.5448 18.1224 13.8721 18.9459 12.0711 19.1818C10.2702 19.4176 8.44176 19.0525 6.86949 18.1431L3.65474 19.2156C3.53597 19.2553 3.40862 19.2617 3.28645 19.2343C3.16427 19.2069 3.05191 19.1466 2.96148 19.06C2.87105 18.9734 2.80599 18.8637 2.77332 18.7429C2.74065 18.622 2.74162 18.4945 2.77611 18.3741L3.75511 14.949C3.09514 13.7374 2.74956 12.3797 2.74999 11ZM11 4.125C9.78611 4.12492 8.59385 4.44624 7.54441 5.05629C6.49497 5.66634 5.62576 6.54338 5.02513 7.59825C4.42451 8.65311 4.11388 9.8482 4.12483 11.062C4.13579 12.2759 4.46792 13.4651 5.08749 14.509C5.13515 14.5896 5.16584 14.6792 5.17766 14.7721C5.18949 14.865 5.18219 14.9594 5.15624 15.0494L4.45636 17.4969L6.73611 16.7379C6.83293 16.7056 6.93573 16.6952 7.03705 16.7076C7.13836 16.7199 7.23566 16.7547 7.32186 16.8094C8.22286 17.3796 9.24413 17.7324 10.305 17.8399C11.3658 17.9475 12.4371 17.8068 13.4342 17.429C14.4313 17.0512 15.3269 16.4467 16.0501 15.6632C16.7733 14.8797 17.3044 13.9388 17.6014 12.9147C17.8983 11.8906 17.953 10.8115 17.7611 9.76263C17.5692 8.71378 17.136 7.72393 16.4956 6.87134C15.8553 6.01876 15.0255 5.32679 14.0717 4.85015C13.1178 4.3735 12.0663 4.12525 11 4.125Z"
            fill="var(--color-foreground-strong)"
          />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <div
            className={cn(
              'fixed bottom-20 left-4 right-4 z-50 sm:bottom-28',
              isBottomLeft
                ? 'sm:left-8 sm:right-auto'
                : 'sm:left-auto sm:right-8'
            )}
          >
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.9,
                y: 20,
                transformOrigin: isBottomLeft ? 'bottom left' : 'bottom right',
              }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                'relative flex h-[min(460px,calc(100vh-120px))] w-full max-w-[441px] flex-col overflow-hidden rounded-[24px] border border-[var(--color-cloud-150)] bg-white shadow-[0px_8px_32px_0px_#0000000d] sm:h-[min(596px,calc(100vh-160px))] sm:w-[441px]',
                isBottomLeft ? 'mr-auto' : 'ml-auto'
              )}
            >
              <div className="flex items-start justify-between border-b border-[var(--color-cloud-150)] p-3 sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={avatar}
                      alt=""
                      aria-hidden="true"
                      width={56}
                      height={56}
                      decoding="async"
                      className="h-10 w-10 rounded-xl object-cover sm:h-[56px] sm:w-[56px] sm:rounded-2xl"
                    />
                    <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-4 border-white bg-[#2ecc71]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold leading-[24px] text-[var(--color-foreground)] sm:text-lg">
                      {agentName}
                    </h3>
                    <p className="text-[0.75rem] font-medium text-[#bdc3c7]">
                      Online
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-[var(--color-foreground-emphasis)] transition-colors hover:text-gray-900"
                  aria-label={`Close ${agentName} assistant`}
                >
                  <Minus className="w-6" />
                </button>
              </div>

              <div
                ref={messagesContainerRef}
                className="scrollbar-hide flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:space-y-6 sm:px-6"
              >
                <div className="flex flex-col gap-4">
                  <div className="max-w-[85%] self-start rounded-2xl rounded-tl-[6px] bg-[var(--color-cloud-50)] p-4">
                    <p className="text-strong-950 text-xs leading-[24px] sm:text-sm">
                      {resolvedWelcome}
                    </p>
                  </div>

                  {visibleMessages.length === 0 &&
                    suggestedActions.length > 0 && (
                      <div className="mt-2 space-y-3">
                        <p className="text-xs font-medium tracking-[0px] text-[var(--color-neutral-400)]">
                          Suggested actions:
                        </p>
                        <div className="flex flex-col gap-2.5">
                          {suggestedActions.map(action => (
                            <button
                              type="button"
                              key={action}
                              onClick={() => handleSendMessage(action)}
                              disabled={isAgentLoading}
                              className="text-sub-600 flex h-10 w-fit items-center justify-center rounded-[14px] border border-[var(--color-border-default)] px-3 text-left text-xs font-medium transition-all hover:bg-[#f8f9fa] disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm md:h-[45px]"
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  {visibleMessages.map(msg => {
                    const isUser = msg.sender === 'user';
                    const content = getStreamableMessageContent(msg);

                    return isUser ? (
                      <div
                        key={msg.id}
                        data-chat-message-id={msg.id}
                        className="max-w-[85%] self-end rounded-2xl rounded-tr-[6px] bg-[#525866] px-3 py-2 sm:p-4"
                      >
                        <p className="text-xs font-normal leading-[24px] text-white sm:text-[15px]">
                          {content}
                        </p>
                      </div>
                    ) : (
                      <div
                        key={msg.id}
                        data-chat-message-id={msg.id}
                        className="max-w-[85%] self-start rounded-2xl rounded-tl-[6px] bg-[var(--color-cloud-50)] px-3 py-2 sm:p-4"
                      >
                        <StreamingAssistantMessage message={msg} />
                      </div>
                    );
                  })}

                  {isAgentLoading &&
                    !visibleMessages.some(
                      msg => msg.sender !== 'user' && msg.isStreaming
                    ) && (
                      <TypingIndicator
                        agentImageSrc={avatar}
                        agentName={agentName}
                      />
                    )}
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="border-t border-[var(--color-cloud-150)] p-4"
              >
                <div className="group relative">
                  <textarea
                    ref={textareaRef}
                    placeholder="Ask me anything..."
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleTextareaKeyDown}
                    disabled={isAgentLoading}
                    rows={1}
                    className="max-h-36 min-h-[48px] w-full resize-none appearance-none overflow-y-auto rounded-xl border border-[var(--color-border-default)] bg-[var(--color-gray-50)] py-3 pl-4 pr-14 text-base leading-6 text-gray-600 outline-none transition-all placeholder:text-[#0a0a0a80] focus:border-[var(--color-border-default)] focus:outline-none focus:ring-2 focus:ring-[#3498db]/20 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-[58px] sm:rounded-[14px] sm:py-4 sm:pl-6"
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isAgentLoading}
                    className="absolute bottom-2.5 right-4 flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#f7f7f7] transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 sm:bottom-4"
                    aria-label="Send message"
                  >
                    <Icons.Send className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
